import React from "react";
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Alert,
  Divider,
} from "@mui/material";
import {
  Share as ShareIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  Notifications as NotificationsIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { isSafariIOS, isChromeIOS } from "../../utils/browserDetection";

interface IOSInstallInstructionsProps {
  onClose?: () => void;
}

const IOSInstallInstructions: React.FC<IOSInstallInstructionsProps> = ({
  onClose,
}) => {
  const isSafari = isSafariIOS();
  const isChrome = isChromeIOS();

  const getSteps = () => {
    if (isSafari) {
      return [
        {
          label: "Appuyez sur le bouton de partage",
          description:
            "Trouvez le bouton carré avec une flèche vers le haut en bas de l'écran Safari",
          icon: <ShareIcon />,
        },
        {
          label: "Sélectionnez « Sur l'écran d'accueil »",
          description:
            "Faites défiler dans le menu de partage et appuyez sur « Sur l'écran d'accueil »",
          icon: <HomeIcon />,
        },
        {
          label: "Confirmez l'ajout",
          description:
            "Appuyez sur « Ajouter » en haut à droite pour confirmer l'installation",
          icon: <CheckCircleIcon />,
        },
        {
          label: "Ouvrez l'application installée",
          description:
            "Retournez à l'écran d'accueil et ouvrez l'application depuis l'icône installée",
          icon: <HomeIcon />,
        },
        {
          label: "Activez les notifications",
          description:
            "Une fois dans l'application installée, revenez sur cette page pour activer les notifications",
          icon: <NotificationsIcon />,
        },
      ];
    }

    if (isChrome) {
      return [
        {
          label: "Appuyez sur le menu Chrome",
          description:
            "Appuyez sur les trois points en haut à droite de Chrome",
          icon: <ShareIcon />,
        },
        {
          label: "Sélectionnez « Ajouter à l'écran d'accueil »",
          description:
            "Dans le menu, appuyez sur « Ajouter à l'écran d'accueil »",
          icon: <HomeIcon />,
        },
        {
          label: "Confirmez l'ajout",
          description: "Appuyez sur « Ajouter » pour confirmer l'installation",
          icon: <CheckCircleIcon />,
        },
        {
          label: "Ouvrez l'application installée",
          description:
            "Retournez à l'écran d'accueil et ouvrez l'application depuis l'icône installée",
          icon: <HomeIcon />,
        },
        {
          label: "Activez les notifications",
          description:
            "Une fois dans l'application installée, revenez sur cette page pour activer les notifications",
          icon: <NotificationsIcon />,
        },
      ];
    }

    // Instructions génériques pour iOS
    return [
      {
        label: "Ouvrez le menu de partage",
        description: "Appuyez sur le bouton de partage de votre navigateur",
        icon: <ShareIcon />,
      },
      {
        label: "Ajoutez à l'écran d'accueil",
        description: "Sélectionnez l'option pour ajouter à l'écran d'accueil",
        icon: <HomeIcon />,
      },
      {
        label: "Confirmez l'installation",
        description: "Confirmez l'ajout de l'application",
        icon: <CheckCircleIcon />,
      },
      {
        label: "Ouvrez l'application installée",
        description: "Ouvrez l'application depuis l'écran d'accueil",
        icon: <HomeIcon />,
      },
      {
        label: "Activez les notifications",
        description: "Revenez sur cette page pour activer les notifications",
        icon: <NotificationsIcon />,
      },
    ];
  };

  const steps = getSteps();

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <InfoIcon color="primary" />
        <Typography variant="h6">
          Instructions d'installation détaillées
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Pourquoi installer l'application ?</strong> Sur iOS, les
          notifications ne fonctionnent que lorsque l'application est installée
          sur l'écran d'accueil. Suivez ces étapes pour activer les
          notifications.
        </Typography>
      </Alert>

      <Stepper orientation="vertical">
        {steps.map((step, index) => (
          <Step key={index} active={true}>
            <StepLabel
              StepIconComponent={() => step.icon}
              sx={{
                "& .MuiStepLabel-label": {
                  fontWeight: "bold",
                },
              }}
            >
              {step.label}
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {step.description}
              </Typography>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      <Divider sx={{ my: 3 }} />

      <Alert severity="warning">
        <Typography variant="body2">
          <strong>Important :</strong> Une fois l'application installée, vous
          devrez l'ouvrir depuis l'écran d'accueil (pas depuis Safari) pour que
          les notifications fonctionnent correctement.
        </Typography>
      </Alert>

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button variant="outlined" onClick={onClose}>
          J'ai compris
        </Button>
      </Box>
    </Paper>
  );
};

export default IOSInstallInstructions;
